/**
 * Shared field-type validators for public form answers (Issue #439).
 *
 * Production-grade libraries, mirroring the server-side rules so the client
 * (UX layer) and the server (the control — the public submit endpoint is
 * unauthenticated) stay in sync:
 *
 *   email → Zod z.string().email()            (zod ^3.24.4, already a dep)
 *   url   → Zod z.string().url() + host-dot    (zod)
 *   phone → libphonenumber-js isPossiblePhoneNumber  (JS port of libphonenumber,
 *           same algorithm as the backend `phonenumbers` library)
 *
 * Each validator returns an error STRING (user-facing) or `null` (valid or
 * empty). Empty returns `null` — "required" is enforced separately so a blank
 * optional field never shows a format error.
 */
import { z } from 'zod';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

// Email — Zod's built-in email validation (production-grade).
const emailSchema = z.string().email('Please enter a valid email address');

// URL — Zod's built-in URL validation. Pre-normalization (scheme prepend) and
// the host-dot check are applied in validateUrl() below.
const urlSchema = z.string().url('Please enter a valid URL (e.g., https://example.com)');

// Phone — default region for national numbers (no `+` country code). Mirrors
// the backend `phonenumbers` DEFAULT_REGION. Configurable per-org later.
const PHONE_DEFAULT_REGION = 'US';

const URL_EXAMPLE_MSG = 'Please enter a valid URL (e.g., https://example.com)';

/**
 * Validate an email answer. Returns an error string or null.
 */
export function validateEmail(value) {
    if (!value) return null;
    const result = emailSchema.safeParse(String(value).trim());
    return result.success ? null : (result.error.issues[0]?.message ?? 'Please enter a valid email address');
}

/**
 * Validate a phone answer. Returns an error string or null.
 * Uses libphonenumber-js isPossiblePhoneNumber (lenient — mirrors the backend
 * phonenumbers.is_possible_number with the same default region).
 */
export function validatePhone(value, country) {
    if (!value) return null;
    const cleaned = String(value).trim();
    if (!cleaned) return null;
    // Issue #440: when a country is supplied (from the selector), validate
    // against that country's per-country length rules; otherwise fall back to
    // the US default region (the #439 lenient behavior).
    const region = country || PHONE_DEFAULT_REGION;
    if (!isPossiblePhoneNumber(cleaned, region)) {
        return 'Please enter a valid phone number';
    }
    return null;
}

/**
 * Validate a URL answer. Returns an error string or null.
 * Pre-normalizes scheme-less inputs by prepending https://, rejects non-http(s)
 * schemes (javascript:, data:), and requires a host with a dot.
 */
export function validateUrl(value) {
    if (!value) return null;
    let val = String(value).trim();
    if (!val) return null;

    // Detect an existing scheme. If present it must be http/https; anything
    // else (javascript:, data:, ...) is rejected as an injection surface.
    const schemeMatch = val.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
    if (schemeMatch) {
        if (!/^https?:\/\//i.test(val)) {
            return 'URLs must start with http:// or https://';
        }
    } else if (val.startsWith('//')) {
        // Protocol-relative — prepend https:
        val = 'https:' + val;
    } else {
        // No scheme — normalize by prepending https://
        val = 'https://' + val;
    }

    // Validate the (normalized) URL with Zod.
    const result = urlSchema.safeParse(val);
    if (!result.success) {
        return result.error.issues[0]?.message ?? URL_EXAMPLE_MSG;
    }

    // Require a host with a dot (rejects bare hostnames like "notaurl").
    try {
        const host = new URL(val).hostname;
        if (!host || !host.includes('.')) {
            return URL_EXAMPLE_MSG;
        }
    } catch {
        return URL_EXAMPLE_MSG;
    }
    return null;
}

/**
 * Boolean email check for admin CRM forms (Contacts, ContactDetail,
 * AccountDetail, useInbox). Returns true/false — NOT an error
 * string. Uses the same Zod validation as validateEmail() so admin and public
 * forms share one rule set (F03 dedup — replaces the old regex in
 * utils/emailValidation.js).
 */
export function isValidEmail(value) {
    if (!value || typeof value !== 'string') return false;
    return validateEmail(value) === null;
}

/**
 * Dispatch a single answer to the right validator by field_type.
 * Returns an error string or null. Unknown field types return null (no
 * format validation).
 */
export function validateFieldByType(fieldType, value, country) {
    if (fieldType === 'email') return validateEmail(value);
    if (fieldType === 'phone') {
        // Issue #440: NurturePhoneInput emits E.164 strings. The optional
        // `country` arg is forwarded for per-country length validation when
        // available; validatePhone falls back to the US default region.
        return validatePhone(value, country);
    }
    if (fieldType === 'url') return validateUrl(value);
    return null;
}
